
import { useState, useEffect, useRef } from 'react';
import { loadPhotoFromCache, fetchAndCachePhoto } from '../../../utils/photoCacheService';
import {
  IMAGE_UPLOAD_ACCEPT,
  normalizeImageFile,
  resizeImage,
  uploadPhoto
} from '../../../services/photoStorageService';
import { useSchoolFirestore } from '../../../hooks/useSchoolFirestore';
import { useSchool } from '../../../contexts/SchoolContext';

const Avatar = ({
  firstName,
  surname,
  name,
  photoURL,
  userId,
  size = 'md',
  className = '',
  // New optional props for photo capture
  allowPhotoCapture = false,
  entityType = '',
  entityId = '',
  onPhotoUpdated = null
}) => {
  // Support `name` prop as shorthand — split into firstName/surname
  if (name && !firstName && !surname) {
    const parts = name.trim().split(/\s+/);
    firstName = parts[0] || '';
    surname = parts.length > 1 ? parts[parts.length - 1] : '';
  }
  const [imageError, setImageError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { schoolId } = useSchool();
  const { update } = useSchoolFirestore();

  // Load cached photo synchronously (instant display, no flash)
  const [cachedPhotoURL, setCachedPhotoURL] = useState(() => {
    if (!userId) return null;

    const cached = loadPhotoFromCache(userId, photoURL);
    if (cached) {
      return cached.base64Data;
    }

    return null;
  });

  const initials = `${firstName?.charAt(0) || ''}${surname?.charAt(0) || ''}`.toUpperCase();

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-lg',
    // Legacy support
    small: 'w-10 h-10 text-sm',
    medium: 'w-12 h-12 text-base',
    large: 'w-16 h-16 text-lg'
  };

  // Fetch and cache photo
  useEffect(() => {
    if (!userId) {
      return;
    }

    const cached = loadPhotoFromCache(userId, photoURL);

    // If cached and URL matches, we're done
    if (cached && !cached.needsUpdate) {
      setCachedPhotoURL(cached.base64Data);
      setImageError(false);
      return;
    }

    // If cached but URL changed, keep showing old photo while updating
    if (cached && cached.needsUpdate) {
      setCachedPhotoURL(cached.base64Data);
      setImageError(false);
    }

    // Only fetch if we have a photoURL
    if (!photoURL) {
      return;
    }

    // Fetch in background (whether updating or first load)
    let mounted = true;

    fetchAndCachePhoto(userId, photoURL)
      .then((base64Data) => {
        if (mounted && base64Data) {
          setCachedPhotoURL(base64Data);
          setImageError(false);
        } else if (mounted && !base64Data) {
          if (!cached) {
            // Only clear if we don't have a fallback
            setCachedPhotoURL(null);
          }
          setImageError(true);
        }
      })
      .catch((error) => {
        console.error('[Avatar] ❌ Error fetching photo:', error);
        if (mounted && !cached) {
          setCachedPhotoURL(null);
          setImageError(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, [photoURL, userId]);

  const shouldShowPhoto = cachedPhotoURL && !imageError;

  // Handle photo capture
  const handlePhotoCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !schoolId || !entityType || !entityId) return;

    setIsUploading(true);

    try {
      const imageFile = await normalizeImageFile(file);
      // Resize the image to reduce storage size
      const resizedBlob = await resizeImage(imageFile);
      const resizedFileName = (imageFile.name || 'photo.jpg')
        .replace(/\.heic$/i, '.jpg')
        .replace(/\.heif$/i, '.jpg');
      const resizedFile = new File([resizedBlob], resizedFileName, { type: 'image/jpeg' });

      // Upload to Firebase Storage
      const downloadURL = await uploadPhoto(resizedFile, schoolId, entityType, entityId);

      // Update the Firestore document with new photo URL
      await update(entityType, entityId, { photoURL: downloadURL });

      // Update local state immediately for instant feedback
      setCachedPhotoURL(downloadURL);
      setImageError(false);

      // Call the optional callback
      if (onPhotoUpdated) {
        onPhotoUpdated(downloadURL);
      }
    } catch (error) {
      console.error('[Avatar] Photo upload failed:', error);
      // Optionally show an error message
    } finally {
      setIsUploading(false);
      // Reset file input to allow re-selection of the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePlusClick = () => {
    if (fileInputRef.current && !isUploading) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <div
        className={`
          ${sizeClasses[size] || sizeClasses.md}
          rounded-full
          flex
          items-center
          justify-center
          font-semibold
          flex-shrink-0
          overflow-hidden
          relative
          ${shouldShowPhoto ? 'bg-transparent text-transparent' : 'bg-primary text-primary-content'}
          ${className}
        `}
      >
        {shouldShowPhoto ? (
          <img
            src={cachedPhotoURL}
            alt={`${firstName} ${surname}`}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => {
              setImageError(true);
            }}
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="relative z-10">{initials}</span>
        )}

        {/* Photo capture overlay - only shows when enabled */}
        {allowPhotoCapture && (
          <button
            onClick={handlePlusClick}
            disabled={isUploading}
            className={`
              absolute bottom-0 right-0
              w-5 h-5
              bg-primary text-primary-content
              rounded-full
              flex items-center justify-center
              text-xs font-bold
              shadow-lg
              transition-all
              ${isUploading ? 'opacity-50 cursor-wait' : 'hover:scale-110 cursor-pointer'}
            `}
            style={{
              transform: 'translate(20%, 20%)'
            }}
            aria-label="Capture photo"
          >
            {isUploading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              '+'
            )}
          </button>
        )}
      </div>

      {/* Hidden file input for photo capture */}
      {allowPhotoCapture && (
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_UPLOAD_ACCEPT}
          capture="user"
          onChange={handlePhotoCapture}
          className="hidden"
          disabled={isUploading}
        />
      )}
    </>
  );
};

export default Avatar;
